import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock, MapPin, Truck, User, Phone, Mail, ExternalLink,
  RefreshCw, CircleCheck as CheckCircle, X, TriangleAlert as AlertTriangle,
  Navigation, Package
} from 'lucide-react';
import { PENDING_DELIVERY_REQUESTS_URL, KITCHEN_DELIVERY_RESPONSE_URL } from '../../config/api';
import { authenticatedFetch } from '../Utils/apiHelpers';
import { useOrderSocket, type OrderStatusEvent, type NewOrderEvent } from '../Utils/useOrderSocket';
import { toDate } from '../Utils/time';
import './PendingDeliveryRequestsTab.css';

interface DeliveryRequest {
  order_id: number;
  customer?: {
    name?: string;
    phone?: string;
    email?: string;
    delivery_address?: string;
  };
  delivery_address?: string;
  distance_km?: number;
  distance_miles?: number;
  delivery_fee?: number;
  eta?: string;
  google_map_directions?: string;
  items?: Array<{ food_name: string; quantity: number; price: number; subtotal: number }>;
  total?: number;
  subtotal?: number;
  created_at?: string;
}

interface AcceptedDetails {
  order_id: number;
  customer_details?: {
    name: string;
    phone: string;
    email: string;
    delivery_address: string;
    google_map_link?: string;
  };
  delivery_info?: {
    distance_km: number;
    distance_miles: number;
    delivery_fee: number;
    eta: string;
    google_map_directions?: string;
  };
}

interface PendingDeliveryRequestsTabProps {
  showNotification: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

const DECLINE_REASONS = [
  'Too far to deliver',
  'Not available right now',
  'Kitchen is closing soon',
  'No delivery vehicle available',
  'Other',
];


const PendingDeliveryRequestsTab: React.FC<PendingDeliveryRequestsTabProps> = ({ showNotification }) => {
  const [requests, setRequests] = useState<DeliveryRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [respondingId, setRespondingId] = useState<number | null>(null);

  // Pagination state
  const [pdrPage, setPdrPage] = useState(1);
  const [pdrTotal, setPdrTotal] = useState(0);
  const [loadingMorePdr, setLoadingMorePdr] = useState(false);
  const PDR_PAGE_SIZE = 10;

  // Decline modal state
  const [decliningOrderId, setDecliningOrderId] = useState<number | null>(null);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [otherNote, setOtherNote] = useState('');
  const [declineError, setDeclineError] = useState('');

  // Accepted details display
  const [acceptedDetails, setAcceptedDetails] = useState<AcceptedDetails | null>(null);

  const fetchRequests = useCallback(async (page: number = 1, append: boolean = false) => {
    if (append) setLoadingMorePdr(true);
    try {
      const url = `${PENDING_DELIVERY_REQUESTS_URL}?page=${page}&limit=${PDR_PAGE_SIZE}`;
      const res = await authenticatedFetch(url, {
        method: 'GET',
        headers: { 'accept': 'application/json' },
      });

      if (res.ok) {
        const json = await res.json();
        const allOrders: DeliveryRequest[] = json.orders ?? [];
        const total: number = json.total ?? allOrders.length;
        setPdrTotal(total);
        setRequests(prev => append ? [...prev, ...allOrders] : allOrders);
        setError(null);
      } else if (res.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else {
        setError('Failed to load delivery requests.');
      }
    } catch {
      setError('Network error. Please check your connection.');
    } finally {
      setIsLoading(false);
      setLoadingMorePdr(false);
    }
  }, []);

  const handleLoadMorePdr = () => {
    const nextPage = pdrPage + 1;
    setPdrPage(nextPage);
    fetchRequests(nextPage, true);
  };

  // Real-time updates via WebSocket — replaces 30-second polling.
  // new_order: a new delivery request arrives → prepend and alert.
  // order_status: an order is cancelled/accepted elsewhere → remove from pending.
  const handleNewOrder = useCallback((event: NewOrderEvent) => {
    showNotification('info', 'New Delivery Request', `Order #${event.order_id} needs a delivery response.`);
    fetchRequests(1);
  }, [showNotification, fetchRequests]);

  const handleOrderStatus = useCallback((event: OrderStatusEvent) => {
    const s = (event.status || '').toLowerCase();
    if (s === 'cancelled' || s === 'confirmed' || s === 'accepted') {
      setRequests(prev => prev.filter(r => r.order_id !== event.order_id));
    }
  }, []);

  useOrderSocket({ onNewOrder: handleNewOrder, onOrderStatus: handleOrderStatus });

  useEffect(() => {
    fetchRequests(1);
    // No polling interval — live updates arrive via WebSocket.
  }, [fetchRequests]);

  const handleAccept = async (orderId: number) => {
    setRespondingId(orderId);
    try {
      const res = await authenticatedFetch(KITCHEN_DELIVERY_RESPONSE_URL(orderId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'accept': 'application/json' },
        body: JSON.stringify({ accept: true, decline_reason: '' }),
      });

      if (res.ok) {
        const json = await res.json();
        setRequests(prev => prev.filter(r => r.order_id !== orderId));
        setAcceptedDetails({
          order_id: json.order_id,
          customer_details: json.customer_details,
          delivery_info: json.delivery_info,
        });
        showNotification('success', 'Delivery Accepted', `Order #${orderId} confirmed. You will deliver this order.`);
      } else {
        const errJson = await res.json().catch(() => ({}));
        showNotification('error', 'Accept Failed', errJson.message || 'Could not accept delivery. Please try again.');
      }
    } catch {
      showNotification('error', 'Network Error', 'Please check your connection and try again.');
    } finally {
      setRespondingId(null);
    }
  };

  const openDeclineModal = (orderId: number) => {
    setDecliningOrderId(orderId);
    setSelectedReason(null);
    setOtherNote('');
    setDeclineError('');
  };

  const closeDeclineModal = () => {
    setDecliningOrderId(null);
    setSelectedReason(null);
    setOtherNote('');
    setDeclineError('');
  };

  const handleDeclineSubmit = async () => {
    if (!selectedReason) {
      setDeclineError('Please select a reason for declining.');
      return;
    }

    const reason = selectedReason === 'Other'
      ? (otherNote.trim() || 'Other')
      : selectedReason;

    setRespondingId(decliningOrderId);
    try {
      const res = await authenticatedFetch(KITCHEN_DELIVERY_RESPONSE_URL(decliningOrderId!), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'accept': 'application/json' },
        body: JSON.stringify({ accept: false, decline_reason: reason }),
      });

      if (res.ok) {
        setRequests(prev => prev.filter(r => r.order_id !== decliningOrderId));
        closeDeclineModal();
        showNotification('info', 'Delivery Declined', `Order #${decliningOrderId} will be converted to self-pickup for the customer.`);
      } else {
        const errJson = await res.json().catch(() => ({}));
        showNotification('error', 'Decline Failed', errJson.message || 'Could not decline delivery. Please try again.');
      }
    } catch {
      showNotification('error', 'Network Error', 'Please check your connection and try again.');
    } finally {
      setRespondingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="user-profile-empty-state">
        <div className="user-profile-loading-spinner" style={{ width: '40px', height: '40px', marginBottom: '1rem' }} />
        <p>Loading delivery requests...</p>
      </div>
    );
  }

  if (error && requests.length === 0) {
    return (
      <div className="user-profile-empty-state">
        <AlertTriangle size={60} />
        <p>{error}</p>
        <button onClick={() => { setIsLoading(true); fetchRequests(); }} className="user-profile-edit-btn" style={{ marginTop: '1rem' }}>
          <RefreshCw size={16} />
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="pdr-container">
      <div className="pdr-header">
        <div className="pdr-title-section">
          <h4>Pending Delivery Requests</h4>
          <p>Accept or decline delivery for incoming orders</p>
        </div>
        <button onClick={() => { setIsLoading(true); setPdrPage(1); fetchRequests(1); }} className="user-profile-edit-btn" disabled={isLoading}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {requests.length === 0 ? (
        <div className="user-profile-empty-state">
          <Truck size={60} />
          <p>No pending delivery requests right now. New requests will appear here automatically.</p>
        </div>
      ) : (
        <>
        <div className="pdr-list">
          {requests.map(req => (
            <div key={req.order_id} className="pdr-card">
              <div className="pdr-card-header">
                <div className="pdr-order-id">
                  <Package size={16} />
                  <span>Order #{req.order_id}</span>
                </div>
                {req.created_at && (
                  <span className="pdr-time">
                    <Clock size={13} />
                    {toDate(req.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>

              {req.items && req.items.length > 0 && (
                <div className="pdr-items">
                  {req.items.map((item, idx) => (
                    <div key={idx} className="pdr-item-row">
                      <span className="pdr-item-name">{item.food_name}</span>
                      <span className="pdr-item-qty">x{item.quantity}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="pdr-details">
                <div className="pdr-detail">
                  <MapPin size={14} />
                  <span>{req.customer?.delivery_address || req.delivery_address || 'N/A'}</span>
                </div>
                {(req.distance_miles != null || req.distance_km != null) && (
                  <div className="pdr-detail">
                    <Navigation size={14} />
                    <span>{req.distance_miles != null ? `${req.distance_miles} miles` : `${req.distance_km} km`}</span>
                  </div>
                )}
                {req.eta && (
                  <div className="pdr-detail">
                    <Clock size={14} />
                    <span>ETA: {req.eta}</span>
                  </div>
                )}
                {req.delivery_fee != null && (
                  <div className="pdr-detail">
                    <Truck size={14} />
                    <span>Delivery Fee: &pound;{req.delivery_fee}</span>
                  </div>
                )}
                {req.customer?.name && (
                  <div className="pdr-detail">
                    <User size={14} />
                    <span>{req.customer.name}</span>
                  </div>
                )}
                {req.customer?.phone && (
                  <div className="pdr-detail">
                    <Phone size={14} />
                    <a href={`tel:${req.customer.phone}`}>{req.customer.phone}</a>
                  </div>
                )}
                {req.customer?.email && (
                  <div className="pdr-detail">
                    <Mail size={14} />
                    <a href={`mailto:${req.customer.email}`}>{req.customer.email}</a>
                  </div>
                )}
                {req.total != null && (
                  <div className="pdr-detail pdr-detail--total">
                    <span>Order Total: &pound;{req.total.toFixed(2)}</span>
                  </div>
                )}
              </div>

              {req.google_map_directions && (
                <a
                  href={req.google_map_directions}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pdr-map-link"
                >
                  <ExternalLink size={14} />
                  View Directions on Google Maps
                </a>
              )}

              <div className="pdr-actions">
                <button
                  className="pdr-btn pdr-btn--decline"
                  onClick={() => openDeclineModal(req.order_id)}
                  disabled={respondingId === req.order_id}
                >
                  <X size={16} />
                  Decline
                </button>
                <button
                  className="pdr-btn pdr-btn--accept"
                  onClick={() => handleAccept(req.order_id)}
                  disabled={respondingId === req.order_id}
                >
                  {respondingId === req.order_id ? (
                    <><div className="user-profile-loading-spinner" style={{ width: '16px', height: '16px' }} /><span>Processing...</span></>
                  ) : (
                    <><CheckCircle size={16} /><span>Accept Delivery</span></>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Load More button for pending delivery requests */}
        {requests.length > 0 && requests.length < pdrTotal && (
          <div className="pdr-load-more">
            <button
              className="user-profile-edit-btn"
              onClick={handleLoadMorePdr}
              disabled={loadingMorePdr}
            >
              {loadingMorePdr ? (
                <><div className="user-profile-loading-spinner" style={{ width: '16px', height: '16px' }} /><span>Loading...</span></>
              ) : (
                <><RefreshCw size={16} /><span>Load More</span></>
              )}
            </button>
            <span className="pdr-count">{requests.length} of {pdrTotal} requests</span>
          </div>
        )}
        </>
      )}

      {/* Accepted delivery details overlay */}
      {acceptedDetails && (
        <div className="pdr-overlay" onClick={() => setAcceptedDetails(null)}>
          <div className="pdr-accepted-modal" onClick={e => e.stopPropagation()}>
            <div className="pdr-accepted-header">
              <CheckCircle size={32} className="pdr-accepted-icon" />
              <h3>Delivery Accepted!</h3>
              <p>Order #{acceptedDetails.order_id} is confirmed for delivery.</p>
            </div>

            {acceptedDetails.customer_details && (
              <div className="pdr-accepted-section">
                <h4>Customer Details</h4>
                <div className="pdr-accepted-details">
                  <div className="pdr-accepted-detail">
                    <User size={14} />
                    <span>{acceptedDetails.customer_details.name}</span>
                  </div>
                  <div className="pdr-accepted-detail">
                    <Phone size={14} />
                    <a href={`tel:${acceptedDetails.customer_details.phone}`}>{acceptedDetails.customer_details.phone}</a>
                  </div>
                  <div className="pdr-accepted-detail">
                    <Mail size={14} />
                    <span>{acceptedDetails.customer_details.email}</span>
                  </div>
                  <div className="pdr-accepted-detail">
                    <MapPin size={14} />
                    <span>{acceptedDetails.customer_details.delivery_address}</span>
                  </div>
                  {acceptedDetails.customer_details.google_map_link && (
                    <a
                      href={acceptedDetails.customer_details.google_map_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pdr-map-link"
                    >
                      <ExternalLink size={14} />
                      View Customer Location
                    </a>
                  )}
                </div>
              </div>
            )}

            {acceptedDetails.delivery_info && (
              <div className="pdr-accepted-section">
                <h4>Delivery Info</h4>
                <div className="pdr-accepted-details">
                  <div className="pdr-accepted-detail">
                    <Navigation size={14} />
                    <span>{acceptedDetails.delivery_info.distance_miles} miles</span>
                  </div>
                  <div className="pdr-accepted-detail">
                    <Clock size={14} />
                    <span>ETA: {acceptedDetails.delivery_info.eta}</span>
                  </div>
                  <div className="pdr-accepted-detail">
                    <Truck size={14} />
                    <span>Fee: &pound;{acceptedDetails.delivery_info.delivery_fee}</span>
                  </div>
                  {acceptedDetails.delivery_info.google_map_directions && (
                    <a
                      href={acceptedDetails.delivery_info.google_map_directions}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pdr-map-link"
                    >
                      <ExternalLink size={14} />
                      View Directions
                    </a>
                  )}
                </div>
              </div>
            )}

            <button className="pdr-btn pdr-btn--accept" style={{ width: '100%', marginTop: '1rem' }} onClick={() => setAcceptedDetails(null)}>
              Got It
            </button>
          </div>
        </div>
      )}

      {/* Decline modal */}
      {decliningOrderId !== null && (
        <div className="pdr-overlay" onClick={closeDeclineModal}>
          <div className="pdr-decline-modal" onClick={e => e.stopPropagation()}>
            <div className="pdr-decline-header">
              <h3>Decline Delivery</h3>
              <p>Order #{decliningOrderId} will be converted to self-pickup for the customer.</p>
            </div>

            <div className="pdr-decline-body">
              <label className="pdr-decline-label">Reason for declining *</label>
              <div className="pdr-decline-reasons">
                {DECLINE_REASONS.map(reason => (
                  <label
                    key={reason}
                    className={`pdr-reason-chip${selectedReason === reason ? ' selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="decline_reason"
                      value={reason}
                      checked={selectedReason === reason}
                      onChange={() => { setSelectedReason(reason); setDeclineError(''); }}
                    />
                    <span>{reason}</span>
                  </label>
                ))}
              </div>

              {selectedReason === 'Other' && (
                <textarea
                  className="pdr-decline-note"
                  value={otherNote}
                  onChange={e => setOtherNote(e.target.value)}
                  placeholder="Please provide details (optional)..."
                  rows={3}
                />
              )}

              {declineError && (
                <div className="pdr-decline-error">
                  <AlertTriangle size={14} />
                  <span>{declineError}</span>
                </div>
              )}
            </div>

            <div className="pdr-decline-actions">
              <button className="pdr-btn pdr-btn--cancel" onClick={closeDeclineModal} disabled={respondingId === decliningOrderId}>
                Cancel
              </button>
              <button
                className="pdr-btn pdr-btn--decline"
                onClick={handleDeclineSubmit}
                disabled={respondingId === decliningOrderId}
              >
                {respondingId === decliningOrderId ? (
                  <><div className="user-profile-loading-spinner" style={{ width: '16px', height: '16px' }} /><span>Declining...</span></>
                ) : (
                  <span>Confirm Decline</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingDeliveryRequestsTab;
